'use client';

// ОДИН ИСТОЧНИК РАСКЛАДКИ КАБИНЕТА (ТЗ WEB-LOOK Э0.2, владелец 30.09.2026: «три раскладки»).
//   phone — ≤820px: мобильный сайт и веб внутри приложения;
//   two   — 821–1023px: меню слева, колонки нет, правый лист = экран телефона без нижнего меню;
//   three — ≥1024px: меню, колонка, лист.
// Значение известно с ПЕРВОГО клиентского кадра (useSyncExternalStore), без эффекта: прежний
// useSplitWide первым кадром отдавал false, и при перезагрузке на широком экране эффекты
// успевали открыть панель ПОВЕРХ третьего листа (капкан 10.08.2026) — это чинили четырьмя
// копиями matchMedia по месту. Теперь никто matchMedia сам не зовёт: только эти функции.
// Пороги — те же, что у CSS (cabinet.css: 820/821 и 1023/1024); своих чисел не заводить.
// INVARIANT(scriptera): второго хука/матчмедиа ширины в кабинете нет — только этот файл.
// Канон: ~/projects/WEB-LOOK-TZ.md, Закон 3.
import { createContext, useCallback, useContext, useSyncExternalStore } from 'react';

export type LayoutMode = 'phone' | 'two' | 'three';

// ТЕЛЕФОННЫЙ ВИД — ПО КОНТЕЙНЕРУ, НЕ ПО ОКНУ (ЗАКОН 3; владелец 03.10.2026, CLAUDE.md «Фундамент
// дизайна» п.2–3): панель-стопка справа на широком экране — это телефон (460 в его пропорции), и
// ВНУТРИ неё всё ровно как на телефоне: те же экраны, шапки, листы, стопка. Поэтому у решений
// «рисовать по-телефонному?» два источника: окно ≤820 (раскладка `phone`) ИЛИ мы внутри панели
// (`PhoneLookCtx`, ставит drawer.tsx каждому уровню). В CSS то же самое делает `@container cab`
// (cabinet.css «КОНТЕЙНЕРЫ ТЕЛЕФОННОГО ВИДА»). Приложение (`html.app`) — всегда false: там листы
// рисует натив.
// INVARIANT(scriptera): новые ветки «телефон в браузере» в содержимом слоёв зовут usePhoneLook /
// readPhoneLook, а не readLayoutMode() === 'phone' — иначе панель на десктопе снова разойдётся
// с телефоном. Канон: ~/projects/WEB-LOOK-TZ.md п.4–7, CLAUDE.md «Фундамент дизайна» п.3.
export const PhoneLookCtx = createContext(false);

/** Сколько слоёв стопки открыто сейчас (ставит drawer.tsx): открытие ИЗ слоя = открытие с телефона. */
let framesOpen = 0;
export function setFramesOpen(n: number): void {
  framesOpen = n;
}

/** Телефонный вид в момент вызова (открыватели, обычные руки): окно-телефон или внутри слоя. Не приложение. */
export function readPhoneLook(): boolean {
  if (isAppClient()) return false;
  return readLayoutMode() === 'phone' || framesOpen > 0;
}

/** Телефонный вид хуком: окно-телефон или мы внутри панели-стопки. Не приложение. */
export function usePhoneLook(): boolean {
  const mode = useLayoutMode();
  const inFrame = useContext(PhoneLookCtx);
  const app = useIsApp();
  return !app && (mode === 'phone' || inFrame);
}

const Q_TWO = '(min-width: 821px)';
const Q_THREE = '(min-width: 1024px)';

/** Синхронно по matchMedia; на сервере — 'phone' (ширины сервер не знает). */
export function readLayoutMode(): LayoutMode {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'phone';
  if (window.matchMedia(Q_THREE).matches) return 'three';
  if (window.matchMedia(Q_TWO).matches) return 'two';
  return 'phone';
}

/** Подписка на смену раскладки (оба порога). Возвращает отписку. */
export function subscribeLayoutMode(cb: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const a = window.matchMedia(Q_TWO);
  const b = window.matchMedia(Q_THREE);
  a.addEventListener('change', cb);
  b.addEventListener('change', cb);
  return () => {
    a.removeEventListener('change', cb);
    b.removeEventListener('change', cb);
  };
}

const serverSnapshot = (): LayoutMode => 'phone';

/** «Мы внутри приложения» — по классу `app` на <html> (native-tabs-init.ts, Э0.1), синхронно. */
export function isAppClient(): boolean {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('app');
}
const subscribeNoop = () => () => {};

/**
 * ПРИЗНАК ПРИЛОЖЕНИЯ С СЕРВЕРА (ТЗ WEB-CAUSES М43, кадр владельца 05.10 22:15 «меню не то»).
 * Раньше серверный снимок был «true для всех» («считаем приложением») — и ПЕРВЫЙ кадр после
 * перезагрузки в браузере рисовал меню приложения (залитый значок «Ботов»), а веб-меню
 * подъезжало следующим кадром. Класс `app` ставится только по UA `ScripteraApp`
 * (native-tabs-init.ts), а UA знает и сервер: layout кабинета кладёт признак сюда
 * (CabinetShell → провайдер), и серверная разметка = кадр гидрации = клиент. Провайдера
 * нет (вне кабинета) → прежнее значение true.
 */
export const AppHintContext = createContext(true);

/** Тот же хук с явной подсказкой сервера — для оболочки, которая сама ставит провайдер. */
export function useIsAppHint(hint: boolean): boolean {
  const server = useCallback(() => hint, [hint]);
  return useSyncExternalStore(subscribeNoop, isAppClient, server);
}

/** То же хуком. На сервере и в кадре гидрации — признак из UA запроса (AppHintContext),
 *  поэтому серверная разметка и первый клиентский кадр совпадают с клиентом. */
export function useIsApp(): boolean {
  return useIsAppHint(useContext(AppHintContext));
}

/** Раскладка сейчас: верна с первого клиентского кадра, обновляется на смене ширины. */
export function useLayoutMode(): LayoutMode {
  return useSyncExternalStore(subscribeLayoutMode, readLayoutMode, serverSnapshot);
}

// КОМПЬЮТЕР, А НЕ ТЕЛЕФОН (TARIFF-PRO-2-TZ §3.12, владелец 12.10.2026; с 13.10 — только выбор ТРИГГЕРА полосы «Ботов»:
// на компьютере значок-молния и нажатие вместо жеста и стрелки «потяните вниз»; постоянная полоса снята). Признак — НЕ ширина окна (узкое окно компьютера — всё равно
// компьютер), а устройство: не приложение и есть мышь — `(hover: hover) and (pointer: fine)`. Планшет и телефон
// (сенсор) — false. На сервере и в первом кадре гидратации — false (разметка как у телефона), дальше — правда.
// INVARIANT(scriptera): «компьютер?» решает только этот хук — своих проверок userAgent и matchMedia по месту нет.
const Q_DESK = '(hover: hover) and (pointer: fine)';
function readDeskPointer(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(Q_DESK).matches;
}
function subscribeDeskPointer(cb: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const q = window.matchMedia(Q_DESK);
  q.addEventListener('change', cb);
  return () => q.removeEventListener('change', cb);
}
const deskServer = (): boolean => false;
/** Компьютер с мышью в браузере (не приложение); телефон, планшет, приложение и сервер — false. */
export function useDeskPointer(): boolean {
  const app = useIsApp();
  const desk = useSyncExternalStore(subscribeDeskPointer, readDeskPointer, deskServer);
  return !app && desk;
}

/** ФЛАГИ ЛИСТА ТОЛЬКО ДЛЯ БРАУЗЕРА (ТЗ WEB-LOOK Э2.5, Закон 4 «что видно в приложении — не
 *  меняется»). Направление слоя (`sheet`, `sheetDetent`, `winKey`) в приложении решает натив:
 *  родной стек берёт `sheet` как «снизу или справа», и новый флаг молча перевернул бы экран
 *  в сборках на телефонах. Поэтому решения таблицы Э2.5 ставятся через эту обёртку: в
 *  браузере — флаги, в приложении — пусто (прежний вид). Зовётся В МОМЕНТ открытия. */
export function webOnly<T extends object>(flags: T): Partial<T> {
  return isAppClient() ? {} : flags;
}

/** Имя уровня ЛИСТОВ ОДНОГО БОТА («О боте», «Дубликат», «Запуски», «Не удалось», заметка,
 *  «Поделиться») — ТЗ WEB-LOOK Э2.5. Отличается от 'bot-window' НАМЕРЕННО: у drawer одинаковое
 *  имя уровня значит «обновить тот же уровень на месте», и лист подменил бы окно бота; другое
 *  имя кладёт лист СТОПКОЙ поверх живого окна (а из списка, без окна, — обычным листом). */
export const BOT_SHEET = 'bot-sheet';
